import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, TrendingUp } from "lucide-react";
import { format, parseISO, getDay, getHours, isBefore, startOfWeek } from "date-fns";
import { cn } from "@/lib/utils";

interface Session {
  id: string;
  date: string;
  cancelled: boolean;
  clients: {
    name: string;
    client_type: string;
  } | null;
}

interface GapAnalysisProps {
  sessions: Session[];
  weeksBack: number;
  averageRate: number;
  maxHoursPerWeek: number;
}

const DAYS_FULL = ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WORKING_HOURS = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const WORKING_DAYS = [1, 2, 3, 4, 5, 6];

const GapAnalysis = ({ sessions, weeksBack, averageRate }: GapAnalysisProps) => {
  const pastSessions = useMemo(() => {
    const now = new Date();
    return sessions.filter(s => !s.cancelled && isBefore(parseISO(s.date), now));
  }, [sessions]);

  // Build usage map
  const usageMap = useMemo(() => {
    const usage: Record<string, number> = {};
    const allWeeks = new Set<string>();

    for (const session of pastSessions) {
      const date = parseISO(session.date);
      const dayOfWeek = getDay(date);
      const hour = getHours(date);
      const weekKey = format(startOfWeek(date), "yyyy-MM-dd");
      const slotKey = `${dayOfWeek}-${hour}`;

      allWeeks.add(weekKey);
      usage[slotKey] = (usage[slotKey] || 0) + 1;
    }

    const totalWeeks = Math.max(allWeeks.size, weeksBack);
    const normalized: Record<string, number> = {};
    
    for (const key of Object.keys(usage)) {
      normalized[key] = Math.min(100, (usage[key] / totalWeeks) * 100);
    }

    return normalized;
  }, [pastSessions, weeksBack]);

  // Find best opportunities
  const opportunities = useMemo(() => {
    const results: Array<{ 
      day: number; 
      hour: number; 
      score: number; 
      reason: string;
      context: string;
    }> = [];

    for (const day of WORKING_DAYS) {
      const activeHours = WORKING_HOURS.filter(h => (usageMap[`${day}-${h}`] || 0) >= 25);
      
      for (const hour of WORKING_HOURS) {
        const key = `${day}-${hour}`;
        const currentUsage = usageMap[key] || 0;
        
        if (currentUsage >= 50) continue;

        const hourBefore = usageMap[`${day}-${hour - 1}`] || 0;
        const hourAfter = usageMap[`${day}-${hour + 1}`] || 0;

        let score = 0;
        let reason = "";
        let context = "";

        if (hourBefore >= 50 && hourAfter >= 50) {
          score = 100;
          reason = "Fills gap in schedule";
          context = `Between your ${format(new Date().setHours(hour - 1, 0), "h a")} and ${format(new Date().setHours(hour + 1, 0), "h a")} sessions`;
        } else if (hourBefore >= 50) {
          score = 85;
          reason = "Extends session block";
          context = `Right after your ${format(new Date().setHours(hour - 1, 0), "h a")} session`;
        } else if (hourAfter >= 50) {
          score = 85;
          reason = "Extends session block";
          context = `Right before your ${format(new Date().setHours(hour + 1, 0), "h a")} session`;
        } else if (activeHours.length > 0) {
          const minActive = Math.min(...activeHours);
          const maxActive = Math.max(...activeHours);
          
          if (hour === minActive - 1) {
            score = 70;
            reason = "Starts your day earlier";
            context = `Before your first session at ${format(new Date().setHours(minActive, 0), "h a")}`;
          } else if (hour === maxActive + 1) {
            score = 70;
            reason = "Extends your day";
            context = `After your last session at ${format(new Date().setHours(maxActive, 0), "h a")}`;
          }
        }

        if (score > 0) {
          results.push({ day, hour, score, reason, context });
        }
      }
    }

    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
  }, [usageMap]);

  // Calculate potential
  const monthlyPotential = opportunities.filter(o => o.score >= 70).length * 4 * averageRate;

  if (opportunities.length === 0) {
    return (
      <Card className="glass-card border-0">
        <CardContent className="py-12 text-center">
          <Sparkles className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="font-medium text-muted-foreground">Schedule looks optimized!</p>
          <p className="text-sm text-muted-foreground mt-1">
            No clear opportunities found in the last {weeksBack} weeks.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-success" />
            Top Recommendations
          </CardTitle>
          {monthlyPotential > 0 && (
            <Badge variant="outline" className="text-success border-success/30">
              +${monthlyPotential.toFixed(0)}/mo potential
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {opportunities.map((opp, idx) => (
          <div 
            key={idx}
            className={cn(
              "p-4 rounded-xl border transition-colors",
              opp.score >= 85
                ? "bg-success/5 border-success/20"
                : "bg-card border-border/50"
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">
                    {DAYS_FULL[opp.day]} at {format(new Date().setHours(opp.hour, 0), "h a")}
                  </span>
                  {opp.score >= 85 && (
                    <Badge variant="success" size="sm">
                      Best
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{opp.context}</p>
              </div>
              <span className={cn(
                "text-xs font-medium px-2 py-1 rounded-full shrink-0",
                opp.score >= 85
                  ? "bg-success/10 text-success"
                  : "bg-muted text-muted-foreground"
              )}>
                {opp.reason}
              </span>
            </div>
          </div>
        ))}
        
        <p className="text-xs text-muted-foreground pt-2">
          Based on your schedule patterns from the last {weeksBack} weeks
        </p>
      </CardContent>
    </Card>
  );
};

export default GapAnalysis;
